import React, { MouseEvent, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import DeleteMenu from "../../../../public/images/delete-icon.svg";
import { useTranslation } from "react-i18next";
import logo_orange_png from "app/images/logo-orange.png";
import RemoveFacilityUser from "app/routes/FacilityUsers/RemoveFacilityUser";
import EditFacilityUser from "app/routes/FacilityUsers/EditFacilityUser";
import reorderIcon from "../../../../public/images/ico_reorder.svg";

interface UserItemProps {
  user: {
    avatar: string;
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    email: string;
    role: string;
    isAdmin: boolean;
  };
  onRemove: (user: any) => void;
  onEdit: (user: any) => void;
  isReorderMode?: boolean;
  dragHandleProps?: any;
}

const UserItem = ({ user, onRemove, onEdit, isReorderMode = false, dragHandleProps }: UserItemProps) => {
  const { t } = useTranslation();
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [menuVisible, setMenuVisible] = useState<string | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const handleArrowClick = (index: string) => {
    setMenuVisible(menuVisible === index ? null : index);
  };

  const handleEditUser = (user: any) => {
    onEdit(user);
    setEditModalOpen(false);
  };

  const handleConfirmRemove = () => {
    onRemove(user);
    setRemoveModalOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      actionMenuRef.current &&
      !actionMenuRef.current.contains(event.target as Node)
    ) {
      setMenuVisible(null);
    }
  };

  useEffect(() => {
    document.addEventListener(
      "mousedown",
      handleClickOutside as unknown as EventListener,
    );
    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside as unknown as EventListener,
      );
    };
  }, []);

  const cleanedFirst = user.firstName?.trim() || "";
  const cleanedLast = user.lastName?.trim() || "";

  const fullName =
    cleanedFirst || cleanedLast
      ? `${cleanedFirst} ${cleanedLast}`.trim()
      : t("Not specified");

  const role =
    user.role === "coach" && user.isAdmin ? t("coach - admin") : t(user.role);

  return (
    <div className={styles.container}>
      {isReorderMode && (
        <div className={styles.drag_handle} {...dragHandleProps}>
          <img
            src={reorderIcon}
            alt="reorder"
            className={styles.reorder_icon}
          />
        </div>
      )}
      <img
        className={styles.profile_image}
        src={user.avatar || logo_orange_png}
        alt={"profile_image"}
      />
      <div className={styles.user_info}>
        <div className={styles.name}>{fullName}</div>
        {/* Always rendered, blank for a member with no title: the row is a set of
            columns, and dropping the cell would shift every column beside it. */}
        <div className={`${styles.text} ${styles.title}`}>{user.title?.trim() || ""}</div>
        <div className={`${styles.text} ${styles.email}`}>{user.email}</div>
        <div className={`${styles.text} ${styles.role}`}>{role}</div>
      </div>
      {!isReorderMode && (
        <div className={`${styles.cell} ${styles.iconContainer}`}>
          <div
            className={cls("bullets", styles.cursorPointer)}
            onClick={() => handleArrowClick(user.id)}
          />
          {menuVisible === user.id && (
            <div ref={actionMenuRef} className={styles.actionMenu}>
              {/* The edit modal was already built and wired to the memberships PUT, but
                  nothing opened it. Without this entry a title could only ever be set
                  while adding a user, leaving every existing coach without one. */}
              <button
                onClick={() => {
                  setMenuVisible(null);
                  setEditModalOpen(true);
                }}
                className={styles.editButton}
              >
                <div>{t("Edit")}</div>
              </button>
              <button
                onClick={() => setRemoveModalOpen(true)}
                className={styles.removeButton}
              >
                <img
                  src={DeleteMenu}
                  alt="deleteMenu"
                  className={styles.deleteImage}
                />
                <div style={{ marginLeft: "10px" }}>{t("Delete")}</div>
              </button>
            </div>
          )}
        </div>
      )}
      <RemoveFacilityUser
        isOpen={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        onDelete={handleConfirmRemove}
        userName={fullName}
      />
      <EditFacilityUser
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        user={user}
        onEdit={handleEditUser}
      />
    </div>
  );
};

export default UserItem;
